import type { ApprovalEventDto, OverviewDto } from '@bjj/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../api/client';
import { DataTable } from '../components/DataTable';
import {
  Badge,
  Card,
  ErrorMessage,
  gradedWhen,
  GradeBadge,
  gradeEventText,
  Loading,
  PageHeader,
} from '../components/ui';

export function OverviewPage() {
  const overview = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => api.request<OverviewDto>('/admin/overview'),
  });
  const recent = useQuery({
    queryKey: ['approvals', 'recent'],
    queryFn: () =>
      api.request<{ items: ApprovalEventDto[] }>('/approvals?limit=10').then((r) => r.items),
  });
  const o = overview.data;
  return (
    <>
      <PageHeader title="Overview" subtitle="Grades and curriculum mastery across your academy." />
      <ErrorMessage error={overview.error} />
      {!o ? (
        <Loading />
      ) : (
        <div className="grid cols-4" style={{ marginBottom: 18 }}>
          {[
            ['Active students', o.students.active],
            ['Average completion', `${o.averageCompletionPercent}%`],
            ['Grades given (7 days)', o.approvals.last7Days],
            ['Grades given (30 days)', o.approvals.last30Days],
            ['Active techniques', `${o.techniques.active} / ${o.techniques.total}`],
            ['Instructors & owners', o.instructors],
            ['Pending invitations', o.pendingInvitations],
            ['Suspended / revoked', `${o.students.suspended} / ${o.students.revoked}`],
          ].map(([label, value]) => (
            <div className="card metric" key={String(label)} style={{ marginBottom: 0 }}>
              <div className="value">{value}</div>
              <div className="label">{label}</div>
            </div>
          ))}
        </div>
      )}
      <Card title="Recent grades" actions={<Link to="/approvals">View all</Link>}>
        <DataTable
          rows={recent.data}
          rowKey={(r) => r.id}
          empty="No grades yet."
          columns={[
            { key: 'when', header: 'When', render: gradedWhen },
            {
              key: 'student',
              header: 'Student',
              render: (r) => <Link to={`/students/${r.student.id}`}>{r.student.name}</Link>,
            },
            { key: 'technique', header: 'Technique', render: (r) => r.technique.name },
            {
              key: 'type',
              header: 'Grade',
              render: (r) => (
                <span className="grade-badge">
                  {r.grade ? <GradeBadge grade={r.grade} /> : <Badge value="rescinded" />}
                  {gradeEventText(r)}
                </span>
              ),
            },
            { key: 'by', header: 'Graded by', render: (r) => r.instructor?.name ?? '—' },
          ]}
        />
      </Card>
    </>
  );
}
